/**
 * Comporre e mandare la giocata.
 *
 * Sta fuori dal componente perche' e' la parte che si prova senza disegnare
 * niente, ed e' anche quella dove i difetti costano denaro: un importo nel
 * posto sbagliato diventa una giocata registrata per una cifra diversa da
 * quella che il cliente ha pagato -- e' gia' successo, sei euro registrati
 * come 0,29.
 *
 * La forma e' quella che si aspetta BetslipRequest nel ticket-service. Il
 * TIPO non si dichiara: lo deduce il server dalla STRUTTURA della giocata,
 * che e' l'unico criterio affidabile. Qui si manda solo `systemData` quando
 * c'e' un sistema.
 */

/* Il nome di una selezione lo fa profiloDiGioco, in un posto solo: ci deve
   stare dentro l'evento, perche' l'identificativo del mercato e quello
   dell'esito vengono dal CATALOGO e sono gli stessi su tutte le partite.
   Qui serve per sapere quali esiti partono come fisse. */
/* Con l'estensione: cosi' il banco dei conti (prova/profilo.mjs) puo'
   caricare questo file con node e provare il numero che parte davvero verso
   il server -- quello che diventa l'addebito. Webpack la risolve uguale. */
import {formatoQuota, fuso} from '../../preferenze';
import {formattaOra} from '../../utils/formato';
import { chiaveSelezione, totaleRiga, limiteDelCodice }
  from '../../services/profiloDiGioco.js';
import i18next from 'i18next';

function indirizzoGateway() {
  const env = (typeof window !== 'undefined' && window.env) ? window.env : {};
  return env.REACT_APP_API_URL || 'http://localhost:9001';
}

export function intestazioni() {
  const token = typeof window !== 'undefined'
    ? window.localStorage.getItem('access_token') : null;
  const h = {'Content-Type': 'application/json'};
  if (token) h.Authorization = `Bearer ${token}`;
  const tenant = typeof window !== 'undefined'
    && window.env && window.env.REACT_APP_API_TENANT;
  if (tenant) h.TENANT_ID = tenant;
  h['X-Client-Surface'] = 'FRONTEND';
  return h;
}

/**
 * Un codice unico per QUESTA compilazione della schedina.
 *
 * Serve contro il doppio clic: due invii identici arrivano al server con lo
 * stesso codice e il secondo viene riconosciuto invece di diventare una
 * seconda giocata e un secondo addebito. Nasce quando la schedina si compila
 * e muore quando si svuota.
 */
export function nuovaChiave() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}



/**
 * La giocata nella forma che il server si aspetta.
 *
 * @param importo        per singola e multipla e' l'importo del ticket; per
 *                       l'integrale e' l'importo PER COMBINAZIONE.
 * @param importiSistema per il sistema, l'importo per colonna di ogni
 *                       grandezza scelta.
 */
export function componiRichiesta({
  selezioni,
  tipo,
  importo,
  importiSistema,
  /* Quante colonne di ogni riga prendono il centesimo in piu'. Vedi sotto:
     senza, il totale della riga viene ricostruito per moltiplicazione e i
     centesimi avanzati spariscono. */
  centesimi,
  attive,
  chiaviFisse,
  utente,
  chiave,
  accettaCambioQuota,
  /* L'agenzia ha spuntato "giroconto automatico": se al giocatore manca
     qualcosa, glielo gira lei. Arrivava fin qui e si fermava -- non era
     dichiarato fra gli argomenti, quindi non finiva nella richiesta e il
     server non lo sapeva nemmeno. */
  giroAutomatico,
  combinazioniDi,
}) {
  const fisse = chiaviFisse || [];

  const bets = (selezioni || []).map((evento) => ({
    eventId: evento.eventId,
    eventNumber: evento.eventNumber,
    matchName: evento.matchName,
    competitionName: evento.competitionName,
    markets: (evento.markets || []).map((mercato) => ({
      id: mercato.id,
      marketName: mercato.marketName,
      odds: (mercato.odds || []).map((quota) => ({
        id: quota.id,
        sbv: quota.sbv,
        oddName: quota.oddName,
        // Il segno letto in cima alla colonna, non il nome della squadra.
        segno: quota.segno || quota.oddName,
        // La fissa vale solo sul sistema: altrove non la si manda nemmeno,
        // cosi' il server non deve fidarsi di un campo che non ha senso.
        fissa: tipo === 'sistema'
            && fisse.indexOf(chiaveSelezione(evento, mercato, quota)) !== -1,
        value: quota.value,
      })),
    })),
  }));

  const richiesta = {
    chiaveGiocata: chiave,
    userId: utente,
    acceptOddsChange: Boolean(accettaCambioQuota),
    giroAutomatico: Boolean(giroAutomatico),
    /* COME IL CLIENTE LEGGEVA quote e ore quando ha premuto. Il server
       continua a tenere decimali e UTC: questo serve all'operatore in
       accettazione e a una contestazione ("l'ho giocata a -0.40"). */
    formatoVisto: formatoQuota(),
    fusoVisto: fuso(),
    bets,
  };

  if (tipo === 'sistema') {
    const quanteSelezioni = bets.reduce(
        (n, e) => n + e.markets.reduce((m, x) => m + x.odds.length, 0), 0);

    const systemTypes = (attive || [])
        .filter((k) => Number(importiSistema[k]) > 0)
        .map((k) => {
          const combinazioni = combinazioniDi(k);
          const perColonna = Number(importiSistema[k]);
          /* I CENTESIMI AVANZATI FANNO PARTE DEL TOTALE.
             Due euro su sei colonne non si dividono in parti uguali: la base
             e' 0,33 e restano due centesimi, che vanno a due colonne. Il
             totale della riga e' quindi 0,33 x 6 + 0,02, non 0,33 x 6. Era
             proprio questa moltiplicazione a far registrare 1,98 a chi
             scriveva 2: il server riceveva 1,98 e distribuiva fedelmente
             1,98.
             Il server questi centesimi li sa spartire da solo -- riceve il
             totale della riga e da' un centesimo in piu' alle prime colonne,
             lo stesso conto che si fa qui -- quindi basta dirgli il totale
             giusto. */
          const piu = Number((centesimi || {})[k]) || 0;
          return {
            // "4/5": quattro selezioni per colonna, su cinque. Il
            // denominatore serve al server per accorgersi se sta guardando
            // una giocata diversa da quella che il cliente ha compilato.
            type: `${k}/${quanteSelezioni}`,
            combinationsCount: combinazioni,
            amountPerCombination: arrotonda(perColonna),
            amount: totaleRiga(perColonna, combinazioni, piu),
          };
        });

    const totale = systemTypes.reduce((s, t) => s + t.amount, 0);
    const combinazioni = systemTypes.reduce((s, t) => s + t.combinationsCount, 0);

    richiesta.systemData = {
      systemTypes,
      totalCombinations: combinazioni,
      totalAmount: arrotonda(totale),
    };
    richiesta.amount = arrotonda(totale);
    richiesta.numCombinations = combinazioni;
    return richiesta;
  }

  if (tipo === 'integrale') {
    // L'importo scritto e' PER COMBINAZIONE: il totale e' quello moltiplicato
    // per quante sono. Dichiararli tutti e due, e chiaramente, e' l'unico
    // modo perche' nessuno dei due venga dedotto male.
    const combinazioni = combinazioniDi(null);
    richiesta.amountPerCombination = arrotonda(Number(importo));
    richiesta.numCombinations = combinazioni;
    richiesta.amount = arrotonda(Number(importo) * combinazioni);
    return richiesta;
  }

  richiesta.amount = arrotonda(Number(importo));
  return richiesta;
}

function arrotonda(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

/**
 * Manda la giocata.
 *
 * @returns {Promise<{ok: boolean, ticket?: Object, errore?: Object}>}
 *          `errore` porta `code`, `message` e `status`: il messaggio per il
 *          giocatore e il codice per chi deve capire cos'e' successo.
 */
export async function inviaGiocata(richiesta) {
  const url = `${indirizzoGateway()}/v1/ticket-service/users/`
      + `${encodeURIComponent(richiesta.userId)}/tickets`;

  try {
    const risposta = await fetch(url, {
      method: 'POST',
      headers: intestazioni(),
      body: JSON.stringify(richiesta),
    });

    const corpo = await risposta.json().catch(() => null);

    if (risposta.ok) {
      /* 202 NON E' 200, e tenerle insieme diceva una bugia al giocatore.
         La giocata e' registrata e la posta e' impegnata, ma nessuno l'ha
         accettata: la deve guardare una persona. Il corpo ha la forma del
         catalogo -- code, message, params -- e il codice giocata sta in
         params.codice, non in ticketCode: chi leggeva ticketCode scriveva
         "Giocata registrata · TICKET_RESERVED", cioe' il nome del codice al
         posto del codice. */
      return {ok: true, inAttesa: risposta.status === 202, ticket: corpo};
    }

    return {ok: false, errore: errore(risposta.status, corpo)};
  } catch (e) {
    return {
      ok: false,
      errore: {
        code: 'RETE',
        status: 0,
        message: i18next.t('Non si riesce a raggiungere il server: riprova fra poco'),
      },
    };
  }
}

/**
 * L'errore, con il CODICE e il dettaglio.
 *
 * Il numero da solo non dice niente a chi guarda: un 403 puo' essere dieci
 * cose diverse. Il messaggio e' per il giocatore, il codice per chi deve
 * capire cos'e' successo davvero.
 */
export function errore(stato, corpo) {
  const code = (corpo && corpo.code) || `HTTP_${stato}`;
  const message = (corpo && corpo.message)
      || i18next.t('La giocata non e’ stata accettata');
  const parametri = (corpo && corpo.params) || {};

  /* RIFIUTATA PERCHE' IL BANCO E' CHIUSO, e la skin ha scelto di dirlo
     (Impostazioni schedina, tendina 7: «Banco chiuso e i limiti» o «Banco
     chiuso, i limiti e l'ora»). Non e' un rifiuto come gli altri: la giocata
     non e' nata e il conto non e' stato toccato, e di giorno quella stessa
     giocata sarebbe passata da una persona. Due cose in piu' rispetto a un
     errore qualunque:
       - la frase si riscrive con L'ORA DI CHI GUARDA (vedi fraseBancoChiuso);
       - si dice QUALE limite l'ha fermata, col nome del contratto, cosi' la
         schedina non scrive una seconda volta la stessa cosa. Se il limite
         era su un bersaglio (un mercato, una fascia di quota) non e' uno di
         quelli che la schedina conosce, e non si dice niente.
     Nel modo di serie, «solo i limiti», a banco chiuso il servizio rifiuta
     col codice e la frase DI GIORNO (LIMIT_AMOUNT_MAX, ...): dal sito non si
     distingue, ed e' voluto. Quel caso passa di sotto, come un rifiuto
     qualunque. */
  if (code === 'TICKET_RESERVE_CLOSED') {
    return {
      code,
      status: stato,
      message: fraseBancoChiuso(parametri) || message,
      dettagli: (corpo && corpo.details) || null,
      bancoChiuso: true,
      limite: parametri.bersaglio ? null : limiteDelCodice(parametri.codiceDelLimite),
    };
  }

  /* Il messaggio scritto sul vincolo dall'operatore vince su quello del
     catalogo: e' quello che ha deciso di far leggere al giocatore. */
  const suo = parametri.messaggio;

  return {
    code,
    status: stato,
    message: suo || message,
    dettagli: (corpo && corpo.details) || null,
    /* QUALE LIMITE, anche di giorno: la schedina scrive i massimi sotto
       l'importo e spegne il tasto sopra; se il servizio ha appena rifiutato
       per uno di quelli, la riga sotto il tasto non si ripete, e il profilo
       si richiede (SchedinaCollegata). Un codice che non e' un limite da'
       null, e non cambia niente. */
    limite: limiteDelCodice(code),
  };
}

/**
 * La frase di un rifiuto a banco chiuso, con l'ora di chi guarda.
 *
 * Il servizio la manda gia' composta, ma con l'ora della skin e il fuso
 * scritto accanto -- "Riapre alle 08:00 (Europe/Rome)" -- perche' quella
 * frase finisce anche nel registro. Sul sito tutte le ore sono nel fuso di
 * chi guarda, con la sigla: quindi si prendono le PAROLE del servizio
 * (`dettaglio`: il limite, col numero) e l'ISTANTE in cui riapre, e l'ora la
 * si scrive qui.
 *
 * Senza un istante di riapertura la frase si ferma al limite: un banco
 * chiuso a mano su una skin senza fascia non ha un'ora da dire, e una skin
 * che non ha scelto «Banco chiuso, i limiti e l'ora» (Impostazioni schedina,
 * tendina 7) non la manda proprio -- ne' nella frase ne' in `riapre`.
 *
 * @returns la frase, o '' se il servizio non ha mandato il dettaglio: in quel
 *          caso chi chiama tiene il messaggio del servizio cosi' com'e'.
 */
export function fraseBancoChiuso(parametri) {
  const p = parametri || {};
  if (!p.dettaglio) return '';
  const ora = p.riapre ? formattaOra(p.riapre, 'ora', {sigla: true}) : '';
  return `Banco chiuso. ${p.dettaglio}.${ora ? ` Riapre alle ${ora}.` : ''}`;
}

/** "Importo massimo 500,00 · LIMIT_AMOUNT_MAX (400)" */
export function descriviErrore(e) {
  if (!e) return '';
  return `${e.message} · ${e.code}${e.status ? ` (${e.status})` : ''}`;
}
